import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip
} from 'recharts';
import { Timer } from 'lucide-react';

interface ChartDataPoint {
  month: string;
  questions: number;
  timeSaved?: number;
}

interface LineChartProps {
  data: ChartDataPoint[];
  title: string;
  description: string;
  yTickCount?: number;
}

// Larger blue dot with white center
function CustomDot(props: any) {
  const { cx, cy } = props;
  if (typeof cx !== 'number' || typeof cy !== 'number') return null;
  return (
    <g>
      <circle cx={cx} cy={cy} r={6} fill="#3b82f6" />
      <circle cx={cx} cy={cy} r={3} fill="#ffffff" />
    </g>
  );
}

// Tooltip with time-saved badge
function CustomTooltip({ active, payload }: any) {
  if (!active || !payload || payload.length === 0) return null;
  const item = payload[0];
  const dataPoint = item && item.payload ? (item.payload as ChartDataPoint) : undefined;
  const questions = item && typeof item.value === 'number' ? (item.value as number) : undefined;

  return (
    <div className="rounded-md border bg-white p-3 shadow-sm">
      {typeof questions === 'number' && (
        <div className="mb-2 text-sm">Questions: <span className="font-medium">{questions}</span></div>
      )}
      {dataPoint && typeof dataPoint.timeSaved === 'number' && (
        <span className="inline-flex items-center gap-1 rounded-full border border-blue-200 bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700">
          <Timer className="h-3 w-3" />
          {dataPoint.timeSaved}h saved
        </span>
      )}
    </div>
  );
}

// Solid blue active dot (no white outline)
function ActiveDot(props: any) {
  const { cx, cy } = props;
  if (typeof cx !== 'number' || typeof cy !== 'number') return null;
  return <circle cx={cx} cy={cy} r={6} fill="#3b82f6" stroke="none" />;
}

export function LineChart({ data, yTickCount = 6 }: LineChartProps) {
  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center h-[200px] text-sm text-muted-foreground">
        No data available
      </div>
    );
  }

  const maxQuestions = Math.max(...data.map((d) => d.questions));
  const yAxisMax = Math.max(10, Math.ceil(maxQuestions / 50) * 50);

  return (
    <div className="w-full h-full">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 8, right: 16, bottom: 12, left: 0 }}>
          <defs>
            <linearGradient id="questionsGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.6} />
              <stop offset="70%" stopColor="#3b82f6" stopOpacity={0.2} />
              <stop offset="100%" stopColor="#3b82f6" stopOpacity={0} />
            </linearGradient>
          </defs>

          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey="month"
            axisLine={false}
            tickLine={false}
            tickMargin={12}
            tick={{ fontSize: 13, fontWeight: 700 }}
          />
          <YAxis
            domain={[0, yAxisMax]}
            allowDecimals={false}
            axisLine={false}
            tickLine={false}
            tickCount={yTickCount}
            tickMargin={12}
            tick={{ fontSize: 12, fill: '#6b7280' }}
          />
          <Tooltip content={<CustomTooltip />} wrapperStyle={{ outline: 'none' }} />
          <Area
            type="monotone"
            dataKey="questions"
            stroke="#3b82f6"
            strokeWidth={2.5}
            fill="url(#questionsGradient)"
            dot={<CustomDot />}
            activeDot={<ActiveDot />}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}
